import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { toast } from "sonner";
import {
  Plus, CalendarDays, Clock, CheckCircle, XCircle, AlertCircle, Info,
} from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { useMyLeaves } from "../../hooks";
import api from "../../lib/api";

// Leave type labels for display
const leaveTypeLabels = {
  annual: "Annual Leave",
  sick: "Sick Leave",
  casual: "Casual Leave",
  maternity: "Maternity Leave",
  paternity: "Paternity Leave",
  unpaid: "Unpaid Leave",
  compensatory: "Compensatory Off",
  bereavement: "Bereavement Leave",
  study: "Study Leave",
};

const MyLeaves = () => {
  // Use custom hook for data fetching
  const { leaves, loading, stats, applyLeave } = useMyLeaves();
  
  // Local state for form
  const [dialogOpen, setDialogOpen] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [leaveType, setLeaveType] = useState("");
  const [reason, setReason] = useState("");
  
  // Leave balance state
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [balanceLoading, setBalanceLoading] = useState(true);

  // Fetch leave balances
  const fetchLeaveBalances = useCallback(async () => {
    setBalanceLoading(true);
    try {
      const res = await api.get("/my-leave-balance");
      setLeaveBalances(res.data.balances || []);
    } catch (error) {
      // Handle case where user doesn't have an employee record (admin-only users)
      // Error handled silently - leave balance unavailable
      setLeaveBalances([]);
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeaveBalances();
  }, [fetchLeaveBalances]);

  // Get balance for selected leave type
  const selectedBalance = leaveBalances.find(b => b.leave_type === leaveType);
  
  // Calculate days requested
  const daysRequested = leaveType ? Math.max(1, differenceInDays(endDate, startDate) + 1) : 0;
  
  // Check if insufficient balance
  const insufficientBalance = selectedBalance && selectedBalance.available < daysRequested && leaveType !== "unpaid";

  const handleApply = async () => {
    if (!leaveType) { toast.error("Select a leave type"); return; }
    if (insufficientBalance) {
      toast.error(`Insufficient ${selectedBalance.name} balance. Available: ${selectedBalance.available} days`);
      return;
    }
    try {
      await applyLeave({
        leave_type: leaveType,
        start_date: format(startDate, "yyyy-MM-dd"),
        end_date: format(endDate, "yyyy-MM-dd"),
        reason,
      });
      toast.success("Leave request submitted");
      setDialogOpen(false);
      setLeaveType("");
      setReason("");
      fetchLeaveBalances(); // Refresh balances
    } catch {
      toast.error("Failed to submit leave request");
    }
  };

  return (
    <div className="space-y-6" data-testid="my-leaves-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">My Leaves</h1>
          <p className="text-slate-500">View your leave history and apply for new leaves</p>
        </div>
        <Button className="btn-primary" onClick={() => setDialogOpen(true)} data-testid="apply-leave-btn">
          <Plus className="h-4 w-4 mr-2" /> Apply for Leave
        </Button>
      </div>

      {/* Leave Balance Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {balanceLoading ? (
          <div className="col-span-full flex justify-center py-4">
            <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        ) : leaveBalances.filter(b => b.accrual_rate > 0 || b.fixed_entitlement).slice(0, 6).map((balance) => (
          <Card key={balance.leave_type} className="hover:shadow-sm transition-shadow">
            <CardContent className="p-3">
              <p className="text-xs text-slate-500 truncate">{balance.name}</p>
              <p className="text-xl font-bold text-primary mt-1">{balance.available}</p>
              <p className="text-[10px] text-slate-400">
                {balance.carry_forward_allowed && balance.carry_forward > 0 
                  ? `(incl. ${balance.carry_forward} c/f)` 
                  : `of ${balance.max_balance} max`}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Stats - using hook's stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="stat-card">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-100"><Clock className="h-6 w-6 text-amber-700" /></div>
              <div><p className="text-2xl font-bold text-slate-800">{stats.pending}</p><p className="text-sm text-slate-500">Pending</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-green-100"><CheckCircle className="h-6 w-6 text-green-700" /></div>
              <div><p className="text-2xl font-bold text-slate-800">{stats.approved}</p><p className="text-sm text-slate-500">Approved</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-red-100"><XCircle className="h-6 w-6 text-red-700" /></div>
              <div><p className="text-2xl font-bold text-slate-800">{stats.rejected}</p><p className="text-sm text-slate-500">Rejected</p></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="card-soft" data-testid="my-leaves-table">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>
          ) : leaves.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No leave requests yet. Apply for one!</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">Duration</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">Reason</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leaves.map(leave => (
                  <TableRow key={leave.id} className="hover:bg-slate-50/50" data-testid={`my-leave-${leave.id}`}>
                    <TableCell className="font-medium text-slate-800">
                      {leaveTypeLabels[leave.leave_type] || leave.leave_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      <div className="flex items-center gap-1">
                        <CalendarDays className="h-4 w-4 text-slate-400" />
                        {leave.start_date} to {leave.end_date}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600 max-w-[200px] truncate">{leave.reason || "—"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        ["pending_dept_head", "pending"].includes(leave.status) ? "bg-amber-50 text-amber-700 border-amber-200" :
                        leave.status === "pending_hr" ? "bg-blue-50 text-blue-700 border-blue-200" :
                        leave.status === "approved" ? "bg-green-50 text-green-700 border-green-200" :
                        "bg-red-50 text-red-700 border-red-200"
                      }>
                        {["pending_dept_head", "pending"].includes(leave.status) && <AlertCircle className="h-3 w-3 mr-1" />}
                        {leave.status === "pending_hr" && <Clock className="h-3 w-3 mr-1" />}
                        {leave.status === "approved" && <CheckCircle className="h-3 w-3 mr-1" />}
                        {leave.status === "rejected" && <XCircle className="h-3 w-3 mr-1" />}
                        {leave.status === "pending_dept_head" ? "Pending Dept Head" :
                         leave.status === "pending_hr" ? "Pending HR" :
                         leave.status}
                      </Badge>
                      {leave.dept_head_name && leave.status !== "pending_dept_head" && (
                        <p className="text-[10px] text-slate-400 mt-0.5">Dept: {leave.dept_head_name}</p>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Apply Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" onCloseAutoFocus={e => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-heading">Apply for Leave</DialogTitle>
            <DialogDescription>Submit a new leave request to your manager</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Leave Type</Label>
              <Select value={leaveType} onValueChange={setLeaveType}>
                <SelectTrigger className="input-field" data-testid="ss-leave-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {leaveBalances.map(b => (
                    <SelectItem key={b.leave_type} value={b.leave_type}>
                      <div className="flex items-center justify-between w-full gap-2">
                        <span>{b.name}</span>
                        <Badge variant="outline" className={b.available > 0 ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}>
                          {b.available} days
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Balance Info Card */}
            {selectedBalance && (
              <div className={`p-3 rounded-lg border ${insufficientBalance ? "bg-red-50 border-red-200" : "bg-violet-50 border-violet-200"}`}>
                <div className="flex items-start gap-2">
                  <Info className={`h-4 w-4 mt-0.5 ${insufficientBalance ? "text-red-600" : "text-violet-600"}`} />
                  <div className="text-sm">
                    <p className={`font-medium ${insufficientBalance ? "text-red-700" : "text-violet-700"}`}>
                      {selectedBalance.name} Balance
                    </p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-1 text-xs">
                      <p><span className="text-slate-500">Available:</span> <span className="font-medium">{selectedBalance.available} days</span></p>
                      <p><span className="text-slate-500">Requesting:</span> <span className="font-medium">{daysRequested} day{daysRequested !== 1 ? "s" : ""}</span></p>
                      <p><span className="text-slate-500">Accrual:</span> {selectedBalance.fixed_entitlement ? "Fixed" : `${selectedBalance.accrual_rate} day/month`}</p>
                      <p><span className="text-slate-500">Carry Forward:</span> {selectedBalance.carry_forward_allowed ? `Yes (max ${selectedBalance.max_carry_forward})` : "No"}</p>
                    </div>
                    {insufficientBalance && (
                      <p className="text-red-600 font-medium mt-2">
                        Insufficient balance! You need {daysRequested - selectedBalance.available} more day(s).
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={format(startDate, "yyyy-MM-dd")}
                  onChange={(e) => e.target.value && setStartDate(new Date(e.target.value))}
                  className="input-field"
                  data-testid="ss-leave-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={format(endDate, "yyyy-MM-dd")}
                  onChange={(e) => e.target.value && setEndDate(new Date(e.target.value))}
                  className="input-field"
                  data-testid="ss-leave-end-date"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea value={reason} onChange={e => setReason(e.target.value)}
                placeholder="Enter reason for leave..." className="input-field min-h-[80px]" data-testid="ss-leave-reason" />
            </div>
            <Button 
              onClick={handleApply} 
              className="w-full btn-primary" 
              disabled={insufficientBalance}
              data-testid="ss-submit-leave-btn"
            >
              {insufficientBalance ? "Insufficient Balance" : "Submit Request"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyLeaves;
